import { useEffect, useState } from 'react';

import RichText from '@eneco/components/RichText/RichText';
import { Button, Checkbox, Grid, Heading, InputUsage, RadioGroup, RadioTile, Stack } from '@eneco/sparky';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { PriceSimulationRenderingExtended } from '@sitecore/types/manual/be/PriceSimulationExtended';
import { Controller, useFormContext } from 'react-hook-form';

import { IsPostalCodeInWalloniaOrBrussels } from '../../../utils/addressUtils';
import {
  DEFAULT_INSTALLATION_TOTAL_KVA,
  METER_TYPE_OPTIONS,
  PriceSimulationFormSchema,
} from '../schemas/priceSimulationFormSchema';
import { FlowChildProps } from '../types/PriceSimulationSteps';
import { getTrackableFormValues } from '../utils/getTrackableFormValues';

export const SolarConsumptionStep = ({ nextMainStep }: FlowChildProps) => {
  const { fields } = useContent<PriceSimulationRenderingExtended>();
  const {
    register,
    trigger,
    formState: { errors },
    getValues,
    setValue,
    resetField,
    control,
    watch,
  } = useFormContext<PriceSimulationFormSchema>();
  const { trackEventBE } = useTracking();

  const [choiceError, setChoiceError] = useState<string | undefined>();

  const choice = watch('solarConsumptionChoice');
  const hasDigitalMeter = getValues('meterType') === METER_TYPE_OPTIONS.DIGITAL;
  const hasSingleMeter = getValues('meterSubTypeRegister') === 'Single';

  const isPostalCodeInWalloniaOrBrussels = IsPostalCodeInWalloniaOrBrussels(getValues('postalCode'));
  const solarPanelInstallDate = getValues('solarPanelInstallDate');

  const canUseKwhInputs =
    (hasDigitalMeter && !isPostalCodeInWalloniaOrBrussels) ||
    (hasDigitalMeter && isPostalCodeInWalloniaOrBrussels && solarPanelInstallDate === 'AfterJanuary2024');

  const onChoiceChange = (value: string) => {
    setValue('solarConsumptionChoice', value as 'kva' | 'kwh');
  };

  const onNextClick = async () => {
    let isValid = false;
    if (!choice) {
      setChoiceError(fields.solarPanelsEnergyReturnStep.solarConsumptionList?.value.requiredMessage);
      return;
    }
    if (choice === 'kva') {
      isValid = await trigger('installationTotalKva');
      resetField('offPeakHoursInjection');
      resetField('peakHoursInjection');
      resetField('totalInjection');
    }
    if (choice === 'kwh') {
      if (hasSingleMeter) {
        isValid = await trigger('totalInjection');
      }
      if (!hasSingleMeter) {
        isValid = await trigger('peakHoursInjection');
        isValid = await trigger('offPeakHoursInjection');
      }
      resetField('installationTotalKva');
      resetField('isCheckedUseDefaultInstallationTotalKva');
    }
    if (!isValid) {
      return;
    }
    trackEventBE('pbt_calculation_peak_solar', {
      status: 'click',
      data: getTrackableFormValues(getValues()),
    });
    nextMainStep();
  };

  useEffect(() => {
    if (choice) {
      setChoiceError(undefined);
    }
  }, [choice]);

  useEffect(() => {
    if (!canUseKwhInputs) {
      setValue('solarConsumptionChoice', 'kva');
    }
  }, [canUseKwhInputs, setValue]);

  return (
    <>
      <Stack direction="column" gap="2">
        <Heading as="h1" size="M">
          {fields.solarPanelsEnergyReturnStep.title.value}
        </Heading>
        <div>
          <RichText html={fields.solarPanelsEnergyReturnStep.subTitle.value} />
        </div>
      </Stack>
      {canUseKwhInputs && (
        <RadioGroup
          name={'choice'}
          direction="row"
          label={fields.solarPanelsEnergyReturnStep.solarConsumptionList?.value.label}
          value={choice ?? undefined}
          onValueChange={onChoiceChange}
          error={choiceError}>
          <RadioTile value={'kwh'}>
            {fields.solarPanelsEnergyReturnStep.solarConsumptionList?.value.options.find(
              option => option.name === 'kwh',
            )?.label ?? 'Injectie [not in Sitecore]'}
          </RadioTile>
          <RadioTile value={'kva'}>
            {fields.solarPanelsEnergyReturnStep.solarConsumptionList?.value.options.find(
              option => option.name === 'kva',
            )?.label ?? 'Totaal vermogen [not in Sitecore]'}
          </RadioTile>
        </RadioGroup>
      )}
      {choice && (
        <Grid.Item alignSelf="stretch">
          <Stack gap="4">
            {choice === 'kva' && (
              <>
                <RichText html={fields.solarPanelsEnergyReturnStep.totalPowerDescription.value} />
                <Controller
                  control={control}
                  name={'installationTotalKva'}
                  render={({ field }) => (
                    <InputUsage
                      {...field}
                      value={field.value ?? ''}
                      label={fields.solarPanelsEnergyReturnStep.injectionKvaFormField.value.label}
                      placeholder={fields.solarPanelsEnergyReturnStep.injectionKvaFormField.value.placeholder}
                      hint={fields.solarPanelsEnergyReturnStep.injectionKvaFormField.value.hint}
                      type="redelivery"
                      unit="kVA"
                      {...(errors.installationTotalKva && { error: errors.installationTotalKva.message })}
                      onChange={event => {
                        field.onChange(event.target.value);
                        if (event.target.value !== DEFAULT_INSTALLATION_TOTAL_KVA.toString()) {
                          setValue('isCheckedUseDefaultInstallationTotalKva', false);
                        }
                      }}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name={'isCheckedUseDefaultInstallationTotalKva'}
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      label={fields.solarPanelsEnergyReturnStep.injectionCapacityUnknownFormField.value.label}
                      hint={
                        <RichText
                          html={fields.solarPanelsEnergyReturnStep.injectionCapacityUnknownFormField.value.hint}
                        />
                      }
                      error={errors.isCheckedUseDefaultPeakConsumption?.message}
                      isChecked={field.value}
                      onChange={event => {
                        if (event.target.checked) {
                          setValue('installationTotalKva', DEFAULT_INSTALLATION_TOTAL_KVA);
                        }
                        field.onChange(event.target.checked);
                      }}
                    />
                  )}
                />
              </>
            )}
            {choice === 'kwh' && <RichText html={fields.solarPanelsEnergyReturnStep.injectionDescription.value} />}
            {choice === 'kwh' && hasSingleMeter && (
              <InputUsage
                {...register('totalInjection')}
                label={fields.solarPanelsEnergyReturnStep.injectionSingleFormField.value.label}
                placeholder={fields.solarPanelsEnergyReturnStep.injectionSingleFormField.value.placeholder}
                hint={fields.solarPanelsEnergyReturnStep.injectionSingleFormField.value.hint}
                type="redelivery"
                unit="kWh"
                {...(errors.totalInjection && { error: errors.totalInjection.message })}
              />
            )}
            {choice === 'kwh' && !hasSingleMeter && (
              <>
                <InputUsage
                  {...register('peakHoursInjection')}
                  label={fields.solarPanelsEnergyReturnStep.injectionPeakHoursFormField.value.label}
                  placeholder={fields.solarPanelsEnergyReturnStep.injectionPeakHoursFormField.value.placeholder}
                  hint={fields.solarPanelsEnergyReturnStep.injectionPeakHoursFormField.value.hint}
                  type="redelivery"
                  unit="kWh"
                  {...(errors.peakHoursInjection && { error: errors.peakHoursInjection.message })}
                />
                <InputUsage
                  {...register('offPeakHoursInjection')}
                  label={fields.solarPanelsEnergyReturnStep.injectionOffPeakHoursFormField.value.label}
                  placeholder={fields.solarPanelsEnergyReturnStep.injectionOffPeakHoursFormField.value.placeholder}
                  hint={fields.solarPanelsEnergyReturnStep.injectionOffPeakHoursFormField.value.hint}
                  type="redelivery"
                  unit="kWh"
                  {...(errors.offPeakHoursInjection && { error: errors.offPeakHoursInjection.message })}
                />
              </>
            )}
          </Stack>
        </Grid.Item>
      )}
      <div>
        <RichText html={fields.solarPanelsEnergyReturnStep.description.value} />
      </div>
      <Button onClick={onNextClick}>{fields.navigation.nextButtonText.value}</Button>
    </>
  );
};
